import React, { useState, useEffect } from 'react';
import { Button, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import DropZone from 'react-dropzone';
import swal from '@sweetalert/with-react';
import FileRow from '../../FileRow/FileRow';
import {
  uploadNFeDuplicata,
  saveCartaCorrecaoFilesURL,
  getCartasCorrecao,
  deleteCartaCorrecaoFile
} from '../../../../../api/borderos';
import { TAMANHO_MAXIMO_UPLOAD } from '../../../../../shared';
import sanitizeFileName from '../../../../../utils/sanitizeFileName';

const ModalImportarCartaCorrecao = props => {
  const { toggle, modal, entrada, updateBordero, updateFileStatus } = props;
  const [files, setFiles] = useState([]); // armazena o arquivo que será dado o upload
  const [, setFileUploadProgress] = useState({});
  const fileTypesAccepted = [
    // array contendo os mime types de arquivos permitidos
    '.xml',
    '.pdf'
  ];

  useEffect(() => {
    getCartasCorrecao(entrada.id, modal.titulo.id)
      .then(res => {
        setFiles(res.data || []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    entrada.arquivos = files;
    updateBordero(entrada);
  }, [files]);

  /**
   * Trata o arquivo que foi jogado/carregado via drodzone
   * Armazena ele no files, para que quando o usuário clicar em "Importar" seja feito o upload no server
   *
   * @param acceptedFiles
   */
  const filesAcceptedHandler = acceptedFiles => {
    const parsedFile = acceptedFiles.map(acceptedFile => {
      const sanitizedName = sanitizeFileName(acceptedFile.name);
      const sanitizedFile = new File([acceptedFile], sanitizedName, {
        type: acceptedFile.type,
        lastModified: acceptedFile.lastModified
      });
      const data = new FormData();
      data.append('file', sanitizedFile);

      const uniqueId = Math.random()
        .toString(36)
        .substr(2, 9);

      return {
        id: uniqueId,
        data,
        name: sanitizedName,
        statusCode: 0,
        response: null,
        alreadyUploaded: false
      };
    });

    setFiles([...files, ...parsedFile]);

    const parseArquivos = [];
    let _processados = 0;

    // percorre todos os arquivos salvos no state (listados)
    parsedFile.forEach(file => {
      // se o arquivo possuir prop data e ainda não tiver efetuado o upload, envia para o servidor
      // somente arquivos que serão dados upload que possuem propriedade 'data'
      if (!file.alreadyUploaded && isNaN(file.id)) {
        uploadNFeDuplicata(file.data, progressValue => {
          file.progress = progressValue;
          file.statusCode = 0;

          setFileUploadProgress({ [file.id]: progressValue });
        })
          .then(res => {
            file.response = res.data;
            file.statusCode = res.status;
            file.alreadyUploaded = true;

            // só atualiza state 'files' caso envio do arquivo seja bem sucedido
            //modificado para ler os arquivos que vier em array
            file.response.forEach(arq => {
              parseArquivos.push({
                arquivo: arq.arquivoSalvo
              });
            });
          })
          .catch(err => {
            file.alreadyUploaded = false;
            file.response = err.response || 'Falha';
            file.statusCode = (err.response && err.response.status) || 400;
          })
          .finally(() => {
            _processados++; // incrementa quantos arquivos foram processados, para executar envio da URL apenas na última requisição

            if (_processados === acceptedFiles.length) {
              setFiles([...files, ...parsedFile]);

              saveCartaCorrecaoFilesURL(entrada.id, modal.titulo.id, parseArquivos).then(response => {
                entrada.arquivos = response.data;
                setFiles(entrada.arquivos);
                updateBordero();

                updateFileStatus(entrada.arquivos && entrada.arquivos.length);
              });
            }
          });
      } else {
        _processados++;
      }
    });
  };

  const filesRejectedHandler = files => {
    const rejectedMessage = files.map(file => {
      const fileName = file.name;
      const extension = fileName.split('.');

      if (fileTypesAccepted.indexOf(`.${extension[extension.length - 1]}`) === -1) {
        return 'Extensão permitida: xml';
      }

      return 'Ocorreu algo inesperado! Verifique o arquivo enviado e tente novamente.';
    });

    swal({
      title: 'Atenção!',
      text: rejectedMessage[0],
      icon: 'error',
      dangerMode: true
    });
  };

  const deleteFileCartaCorrecao = async (file, idFile) => {
    await swal({
      title: 'Atenção!',
      text: 'Deseja apagar a carta de Correção atrelada ao Título?',
      icon: 'warning',
      buttons: ['Cancelar', 'Confirmar']
    }).then(confirm => {
      if (confirm) {
        const updatedFiles = files.filter(fl => fl.id !== file.id);

        if (idFile && !isNaN(idFile)) {
          deleteCartaCorrecaoFile(entrada.id, idFile)
            .then(() => {
              swal({
                title: 'Exclusão',
                text: 'Carta de correção apagada com sucesso!',
                icon: 'success'
              });

              setFiles([...updatedFiles]);
              updateFileStatus(updatedFiles && updatedFiles.length);
              updateBordero();
            })
            .catch(() => {
              swal({
                title: 'Erro',
                text: 'Erro ao apagar a Carta de correção!',
                icon: 'error'
              });
            });
        }
      }
    });
  };

  const retryUpload = file => {
    uploadNFeDuplicata(file.data, progressValue => {
      file.progress = progressValue;
      file.statusCode = 0;
      setFiles([...files]);
    })
      .then(res => {
        file.response = res.data;
        file.statusCode = res.status;
      })
      .catch(err => {
        file.response = err.response;
        file.statusCode = err.response.status;
      })
      .finally(() => {
        setFiles([...files]);
      });
  };

  return (
    <Modal isOpen={modal.show} toggle={toggle} size="md" className="modalDuplicata">
      <ModalHeader toggle={toggle}>Importar Carta de Correção</ModalHeader>
      <ModalBody>
        <Label className="custom-label mb-3">Importe aqui a Carta de Correção do título.</Label>
        <DropZone
          onDropAccepted={filesAcceptedHandler}
          onDropRejected={filesRejectedHandler}
          noDrag={false}
          accept={fileTypesAccepted}
          maxSize={TAMANHO_MAXIMO_UPLOAD}
        >
          {({ getRootProps, getInputProps, isDragActive }) => (
            <section>
              <div {...getRootProps()} className="dropzone">
                <input {...getInputProps()} />
                {isDragActive ? (
                  <h4>Solte o arquivo aqui...</h4>
                ) : (
                  <h4>
                    <span>
                      <i className="fas fa-paperclip file-icon" />
                    </span>
                    <span className="link">Selecione</span>
                    <span> o arquivo ou arraste aqui</span>
                  </h4>
                )}
              </div>
            </section>
          )}
        </DropZone>
        {files.length > 0 && (
          <div className="file-wrapper">
            {files.map((file, key) => (
              <FileRow
                // eslint-disable-next-line react/no-array-index-key
                key={`arquivo-duplicata-${key}`}
                file={file}
                deleteFile={deleteFileCartaCorrecao}
                retryUpload={retryUpload}
              />
            ))}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="primary" size="md" type="button" onClick={toggle}>
          Fechar
        </Button>
      </ModalFooter>
    </Modal>
  );
};

ModalImportarCartaCorrecao.defaultProps = {
  modal: { show: false }
};

export default ModalImportarCartaCorrecao;
